import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Logo } from "../../components/Logo";
import { Select } from "../../components/Select";
import { Body, Heading, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import loginStyles from "../Login/Login.module.css";

export const Recover = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [phase, setPhase] = useState<"request" | "reset">("request");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleRequest = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.recoverStart(email, channel);
      setPhase("reset");
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (e.body?.error ?? e.message)
          : "Couldn't start recovery. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.recoverComplete(email, code.trim(), password);
      navigate("/login", { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't reset. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "reset") {
    return (
      <main className={loginStyles.page}>
        <div className={loginStyles.card}>
          <div className={loginStyles.brand}>
            <Logo />
          </div>
          <div className={loginStyles.header}>
            <Heading level={2}>Enter your code</Heading>
            <Body>
              <Muted>
                If an account matches, a code was sent by {channel === "sms" ? "text" : "email"}.
              </Muted>
            </Body>
          </div>

          <form className={loginStyles.form} onSubmit={handleReset} noValidate>
            <div className={loginStyles.field}>
              <label className={loginStyles.label} htmlFor="code">
                Recovery code
              </label>
              <Input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={submitting}
                invalid={error !== null}
              />
            </div>
            <div className={loginStyles.field}>
              <label className={loginStyles.label} htmlFor="new_password">
                New password
              </label>
              <Input
                id="new_password"
                name="new_password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                invalid={error !== null}
              />
            </div>
            {error && (
              <div role="alert" className={loginStyles.error}>
                {error}
              </div>
            )}
            <Button type="submit" disabled={submitting || code.trim().length === 0}>
              {submitting ? "Resetting…" : "Reset password"}
            </Button>
          </form>

          <div className={loginStyles.footer}>
            <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={loginStyles.page}>
      <div className={loginStyles.card}>
        <div className={loginStyles.brand}>
          <Logo />
        </div>
        <div className={loginStyles.header}>
          <Heading level={2}>Reset your password</Heading>
          <Body>
            <Muted>We'll send a one-time code to a verified contact on your account.</Muted>
          </Body>
        </div>

        <form className={loginStyles.form} onSubmit={handleRequest} noValidate>
          <div className={loginStyles.field}>
            <label className={loginStyles.label} htmlFor="email">
              Account email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              invalid={error !== null}
            />
          </div>
          <div className={loginStyles.field}>
            <label className={loginStyles.label} htmlFor="channel">
              Send the code by
            </label>
            <Select
              id="channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value as "email" | "sms")}
              disabled={submitting}
            >
              <option value="email">Email</option>
              <option value="sms">Text message (verified phone)</option>
            </Select>
          </div>
          {error && (
            <div role="alert" className={loginStyles.error}>
              {error}
            </div>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? "Sending…" : "Send code"}
          </Button>
        </form>

        <div className={loginStyles.footer}>
          Remembered it? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </main>
  );
};
